/**
 * Grouping of per-day breakdown items into consecutive ranges, used by the
 * BalanceTime summary tooltips so a multi-day booking reads as one row
 * ("20.02–27.02 Põhipuhkus") instead of one row per day.
 */

export interface DateItem {
    /** ISO date, yyyy-mm-dd. */
    date: string
    text: string
}

export interface DateRangeItem {
    startDate: string
    endDate: string
    text: string
}

const DAY_MS = 24 * 60 * 60 * 1000

const toUtcMs = (iso: string) => Date.parse(`${iso}T00:00:00Z`)

const isWeekend = (ms: number) => {
    const dow = new Date(ms).getUTCDay()
    return dow === 0 || dow === 6
}

// The input only carries counted days, so weekends and `bridgeDates` (public
// holidays) are simply absent from it. A gap made up solely of such days must
// not break a range, or a booking spanning a weekend splits into pieces.
const isBridgeableGap = (fromIso: string, toIso: string, bridgeDates: ReadonlySet<string>) => {
    const from = toUtcMs(fromIso)
    const to = toUtcMs(toIso)
    if (!(from < to)) return false
    for (let ms = from + DAY_MS; ms < to; ms += DAY_MS) {
        if (isWeekend(ms)) continue
        if (bridgeDates.has(new Date(ms).toISOString().slice(0, 10))) continue
        return false
    }
    return true
}

/** Merges same-text items on consecutive days (weekends/bridge days between count as consecutive). */
export const groupConsecutiveDateItems = (
    items: DateItem[],
    bridgeDates: ReadonlySet<string> = new Set(),
): DateRangeItem[] => {
    const sorted = [...items].sort((a, b) => a.date.localeCompare(b.date))
    const ranges: DateRangeItem[] = []

    for (const item of sorted) {
        const current = ranges[ranges.length - 1]
        if (
            current?.text === item.text &&
            isBridgeableGap(current.endDate, item.date, bridgeDates)
        ) {
            current.endDate = item.date
        } else {
            ranges.push({ startDate: item.date, endDate: item.date, text: item.text })
        }
    }

    return ranges
}
